// Sentry init — env-gated, no-op without VITE_SENTRY_DSN. Done at the
// top so we catch errors during module-load chain (rare but
// devastating when they happen).
import * as Sentry from "@sentry/react";
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event) {
      // Strip any URL query strings — search.global puts user text
      // in `q` query param, which we don't want in error reports.
      if (event.request?.url) {
        event.request.url = event.request.url.split("?")[0];
      }
      return event;
    },
  });
}
import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import { registerServiceWorker } from "./pwa";
import { initNativeBridge } from "./native";

// Tuned defaults — the bare `new QueryClient()` ships staleTime:0 +
// refetchOnWindowFocus:true, which on the native webview is a perf
// footgun: focus/visibility events fire constantly (keyboard show/hide,
// app resume, dialog focus traps), and each one refetched EVERY active
// query (signals + the heavy receipts.forWeek) and re-rendered the whole
// week grid — including any open day-card dialog. Users saw the card
// "freeze, then respond after a while" as those refetch storms piled up
// and the main thread thrashed. These defaults stop the storm: focus
// no longer refetches, data stays fresh for 30s, and failed requests
// retry once instead of three times.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

/**
 * API base URL.
 *
 * - WEB: empty string → relative `/api/trpc`, same-origin against the
 *   deployed host. No CORS, cookie is first-party.
 * - NATIVE (Capacitor iOS/Android): the webview serves the bundled SPA
 *   from `https://localhost` / `capacitor://localhost`, so a relative
 *   URL would resolve to a non-existent local server and EVERY API
 *   call would fail. We must point at the absolute production origin.
 *   `server/_core/capacitorCors.ts` allow-lists the webview origins +
 *   echoes `Access-Control-Allow-Credentials`, and the session cookie
 *   is `SameSite=none; Secure`, so the cross-origin credentialed
 *   request works.
 *
 * The native origin is `VITE_API_BASE_URL` baked at build time (set it
 * in the env before `pnpm build` / `cap sync`), falling back to the
 * known production host so a default `pnpm cap:release` still works.
 *
 * `window.Capacitor` is injected by the native runtime; on web it's
 * undefined, so this check is a safe synchronous no-op there.
 */
function apiBaseUrl(): string {
  const isNative =
    typeof window !== "undefined" &&
    Boolean((window as any).Capacitor?.isNativePlatform?.());
  if (!isNative) return ""; // web → same-origin relative
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(
      /\/$/,
      "",
    ) || "https://receipt1.duckdns.org"
  );
}

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${apiBaseUrl()}/api/trpc`,
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);

// Register the service worker after first paint so it doesn't compete with
// the initial bundle for bandwidth. SSR/dev-mode are no-ops inside the helper.
registerServiceWorker();

// Native-only: register push notifications, wire deep links, etc.
// No-op in the web build via a Capacitor.isNativePlatform() gate.
initNativeBridge(trpcClient as any).catch(err =>
  console.warn("[native] init failed", err),
);
